import { RSAA } from 'redux-api-middleware'

/**
 * This middleware is used to record the API requests and their responses so
 * that we can scrape the data and use it to mock our e2e tests with. The idea
 * is that this shows us all the endpoints that we need to mock for a page or
 * feature.
 *
 * It keeps track of the endpoints called and their SUCCESS type so that we can
 * later pay attention to the SUCCESS type and store the response data.
 *
 * NOTE: The console.log statements are required to be able to see the results.
 */
const successTypes = new Set()
const mockData = {}
export const typeScraper =
  ({ getState }) =>
  next =>
  action => {
    // Remember that if a middleware fn doesn't do anything it needs
    // to return the next action so that the chain of middleware continues.
    if (process.env.ENABLE_API_SCRAPING !== 'true') return next(action)

    console.group('%cSCRAPING', 'color:cyan', action)

    // We'll need to get the state because when the endpoint is a function
    // it uses the state to create the URL.
    const state = getState()

    // If the action is an RSAA action that means that it's an API request.
    // CAUTION: Not all API requests are RSAA actions, but most of them are
    // built though the redux-api-middleware library so it's a safe bet.
    if (RSAA in action) {
      console.group('%cFound RSAA', 'color:darkturquoise')
      const apiAction = action[RSAA]

      // if the endpoint is a string, we can assume it's a URL
      // if the endpoint is a function, we need to run it to get the URL
      const apiEndpoint = _getEndpoint(apiAction, state)
      console.log('%cAPI endpoint', 'color:aquamarine', apiEndpoint)

      // We'll want to grab the SUCCESS type so that we can later pay attention
      // to it and make sure that we store the response/payload data. The
      // SUCCESS type is always the second type in the array. Be careful because
      // it could be either a string or an object with a `type` property. If it
      // has a type property, we'll want to use that.
      const successType = _getSuccessType(apiAction.types)
      console.log('%cSUCCESS type', 'color:aquamarine', successType)
      successTypes.add(successType)
      mockData[apiEndpoint] = successType

      console.groupEnd()
    } else {
      console.log('%cSKIP - NOT API ACTION', 'color:gray')
    }

    console.groupEnd()
    console.log('\n\n')
    return next(action)
  }

/**
 * The response scraper compares the action types to the successTypes that we
 * want to store responses for. If it matches, it stores the response object
 *
 * NOTE: The console.log statements are required to be able to see the results.
 */
const apiResponses = {}
export const responseScraper = () => next => action => {
  // Remember that if a middleware fn doesn't do anything it needs
  // to return the next action so that the chain of middleware continues.
  if (process.env.ENABLE_API_SCRAPING !== 'true') return next(action)

  console.group('%cCHECKING', 'color:forestgreen', action.type)
  console.log('%cAgainst:', 'color:limegreen', successTypes)
  // check if the action type is found in successTypes. If it is, then
  // output the response data.
  if (successTypes.has(action.type)) {
    console.log('%cSAVING', 'color:lime', action.payload)
    apiResponses[action.type] = action.payload
  } else {
    console.log('%cSKIP - NOT SUCCESS TYPE', 'color:gray')
  }
  console.groupEnd()
  console.log('\n\n')

  Object.keys(mockData).forEach(endpoint => {
    if (mockData[endpoint] === action.type) {
      mockData[endpoint] = action.payload
    }
  })
  console.log('%cScraped responses:', 'color:yellow', mockData)

  console.log('\n\n')
  return next(action)
}

/**
 * Helper method to get the name of the SUCCESS type from an RSAA.
 *
 * @param {array} types - The types array from the RSAA action
 * @returns {string} - The SUCCESS type
 */
function _getSuccessType(types) {
  const successType = types[1]
  return typeof successType === 'string' ? successType : successType.type
}

/**
 * Helper method to get the endpoint from an RSAA action.
 *
 * @param {object} apiAction - The RSAA action object
 * @param {object} state - The current redux state
 * @returns {string} - The endpoint URL
 */
function _getEndpoint(apiAction, state) {
  return typeof apiAction.endpoint === 'string'
    ? apiAction.endpoint
    : apiAction.endpoint(state)
}
